import { useEffect, useRef, useState } from 'react';

const kpiData = [
  { value: '2.847', label: 'Günlük Ziyaretçi', icon: 'ri-user-line', color: '#00d9ff' },
  { value: '1.293', label: 'İncelenen Kampanya', icon: 'ri-eye-line', color: '#d4af37' },
  { value: '384', label: 'Oluşan Başvuru', icon: 'ri-file-list-3-line', color: '#00d9ff' },
  { value: '621', label: 'WhatsApp Tıklaması', icon: 'ri-whatsapp-line', color: '#d4af37' },
];

function AnimatedNumber({ target }: { target: string }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const numeric = parseInt(target.replace(/\./g, ''), 10);
    let start = 0;
    const duration = 1800;
    const step = Math.ceil(numeric / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= numeric) {
        start = numeric;
        clearInterval(timer);
      }
      setDisplay(start.toLocaleString('tr-TR'));
    }, 16);
    return () => clearInterval(timer);
  }, [target]);

  return <span ref={ref}>{display}</span>;
}

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0f]"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=ultra%20premium%20luxury%20beauty%20salon%20interior%20dark%20moody%20atmosphere%20with%20golden%20light%20accents%20and%20deep%20navy%20blue%20tones%2C%20elegant%20minimalist%20space%20with%20soft%20bokeh%20background%2C%20high%20end%20aesthetic%20photography%20style%2C%20dark%20background%20with%20warm%20golden%20glow%20and%20subtle%20neon%20blue%20reflections&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="Hero Arka Plan"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/70 via-[#0a0a0f]/50 to-[#0a0a0f]" />
      </div>

      {/* Glow effects */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#d4af37]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/3 left-1/4 w-80 h-80 bg-[#00d9ff]/10 rounded-full blur-[100px] pointer-events-none" />

      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-[#00d9ff]/20 animate-pulse"
            style={{
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col items-center text-center pt-24 pb-8">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d9ff]/40 bg-[#00d9ff]/5 backdrop-blur-sm mb-8 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-2 h-2 flex items-center justify-center">
            <span className="block w-2 h-2 rounded-full bg-[#00d9ff] animate-pulse" />
          </span>
          <span className="text-[#00d9ff] text-xs font-medium tracking-widest uppercase">
            Bu platformdaki her hareket analiz edilir
          </span>
        </div>

        {/* Headline */}
        <h1
          className={`text-4xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6 transition-all duration-700 delay-100 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #d4af37 60%, #f4d03f 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Kampanyalarınızı Sadece<br />
          <span style={{ background: 'linear-gradient(135deg, #00d9ff 0%, #ffffff 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Göstermeyin,
          </span>{' '}
          <span style={{ background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            Müşteriye Dönüştürün
          </span>
        </h1>

        {/* Sub */}
        <p
          className={`text-base md:text-xl text-white/60 max-w-2xl leading-relaxed mb-10 transition-all duration-700 delay-200 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          Ziyaretçilerinizi adım adım yönlendiren ve size başvuru getiren satış odaklı platform
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-col sm:flex-row items-center gap-4 mb-16 transition-all duration-700 delay-300 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            onClick={() => scrollTo('kampanyalar')}
            className="px-8 py-4 rounded-full bg-gradient-to-r from-[#d4af37] to-[#f4d03f] text-black font-bold text-base hover:scale-105 hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Kampanyaları İncele
          </button>
          <a
            href="https://wa.me/905001234567?text=Merhaba%2C%20kampanyalar%20hakk%C4%B1nda%20bilgi%20almak%20istiyorum."
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 rounded-full border-2 border-[#00d9ff]/60 text-[#00d9ff] font-bold text-base hover:bg-[#00d9ff]/10 hover:scale-105 hover:shadow-[0_0_30px_rgba(0,217,255,0.3)] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <i className="ri-whatsapp-line text-lg"></i>
            WhatsApp&apos;tan Yaz
          </a>
        </div>

        {/* KPI Cards */}
        <div
          className={`w-full grid grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {kpiData.map((kpi, i) => (
            <div
              key={i}
              className="group relative rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/40 transition-all duration-300 hover:-translate-y-2 cursor-default"
              style={{
                background: 'rgba(26, 26, 46, 0.6)',
                backdropFilter: 'blur(30px)',
                boxShadow: `0 0 0 1px rgba(255,255,255,0.05)`,
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl"
                style={{ background: `linear-gradient(90deg, transparent, ${kpi.color}, transparent)` }}
              />
              <div className="w-10 h-10 flex items-center justify-center rounded-xl mb-3" style={{ background: `${kpi.color}15` }}>
                <i className={`${kpi.icon} text-xl`} style={{ color: kpi.color }}></i>
              </div>
              <div
                className="text-3xl md:text-4xl font-black mb-1"
                style={{
                  background: `linear-gradient(135deg, #ffffff, ${kpi.color})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                <AnimatedNumber target={kpi.value} />
              </div>
              <div className="text-xs text-white/50 uppercase tracking-widest font-medium">{kpi.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-white/30 text-xs tracking-widest uppercase">Keşfet</span>
        <i className="ri-arrow-down-line text-white/30 text-lg"></i>
      </div>
    </section>
  );
}
